import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../images/Oakland_pic.png';

const PageContainer = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  font-family: 'Roboto', sans-serif;
  overflow: hidden;
`;

const BackgroundImage = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  z-index: -1;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: -1;
  }
`;

const ImageAttribution = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.8rem;
  z-index: 1;
`;

const ContentWrapper = styled(motion.div)`
  max-width: 800px;
  width: 100%;
`;

const Title = styled(motion.h1)`
  color: #1c2f4f;
  margin-bottom: 1rem;
  font-size: 3rem;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.h2)`
  color: #1c2f4f;
  margin-bottom: 2rem;
  font-size: 1.5rem;
  font-weight: normal;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Button = styled(motion.button)`
  background-color: #1c2f4f;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: bold;
  font-family: 'Roboto', sans-serif;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #152238;
  }
`;

const FeatureSection = styled(motion.div)`
  display: flex;
  justify-content: space-around;
  margin-top: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Feature = styled(motion.div)`
  background-color: white;
  padding: 1.5rem;
  border-radius: 10px;
  width: calc(33.333% - 1rem);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FeatureTitle = styled.h3`
  color: #1c2f4f;
  margin-bottom: 0.5rem;
`;

const FeatureDescription = styled.p`
  color: #1c2f4f;
  font-size: 0.9rem;
`;

const HomePage = () => {
  const navigate = useNavigate();

  const handleButtonClick = () => {
    navigate('/meet-the-tutors');
  };

  return (
    <PageContainer>
      <BackgroundImage
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />
      <ImageAttribution>
        Photo by Diane Bentley Raymond/Getty Images
      </ImageAttribution>
      <ContentWrapper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          Hello, Bay Area Mathematicians
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          Need help with math? We offer free tutoring for Oakland middle and high school students.
        </Subtitle>
        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          onClick={handleButtonClick}
        >
          Find Your Perfect Tutor
        </Button>
        <FeatureSection
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
        >
          {[
            { title: "Personalized Learning", description: "Tailored lessons to match YOUR learning style and pace." },
            { title: "Experienced Tutors", description: "Learn from student educators passionate about mathematics." },
            { title: "Flexible Online Scheduling", description: "Pick a time that works for you - weekdays or weekends." }
          ].map((feature, index) => (
            <Feature
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.5 + index * 0.2 }}
            >
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </Feature>
          ))}
        </FeatureSection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default HomePage;